import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../../contexts/LanguageContext";
import Select from "react-select";
import "./CreateVoucher.scss";
import { HiCheck } from "react-icons/hi";
import { PageLayout, ActionButton } from "../../../components/modern";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";

interface CustomerOption {
  value: string;
  label: string;
}

export const CreateVoucher = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['vouchers', 'common']);
  const { isRTL } = useLanguage();

  const [formData, setFormData] = useState({
    code: "",
    voucherType: 0, // 0 = Percent, 1 = Amount
    numberOfUse: 0,
    value: 0,
    maxAmount: -1,
    from: "",
    to: "",
    isActive: false,
    isPublic: false,
    autoApply: false,
    customers: "", // comma separated integers
  });
  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<CustomerOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const token = localStorage.getItem("token");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : name === "voucherType" ? parseInt(value) : value,
    });
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};
    if (!formData.code.trim()) newErrors.code = t('vouchers:validation.codeRequired');
    if (!formData.value || formData.value <= 0) newErrors.value = t('vouchers:validation.valueRequired');
    if (!formData.from) newErrors.from = t('vouchers:validation.fromRequired');
    if (!formData.to) newErrors.to = t('vouchers:validation.toRequired');
    if (!formData.numberOfUse || formData.numberOfUse <= 0) newErrors.numberOfUse = t('vouchers:validation.numberOfUseRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCustomerChange = (selectedOptions: CustomerOption[] | null) => {
    const customers = selectedOptions ? selectedOptions.map(option => option.value).join(",") : "";
    setSelectedCustomers(selectedOptions || []);
    setFormData({
      ...formData,
      customers: customers,
    });
  };
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await adminApi.call('/user/customers/index?pagesize=100000000', 'GET');

        if (!response.success) {
          console.error("Error loading customers:", response.error);
          return;
        }

        // Format data for react-select
        const formattedOptions = response.data?.items.map((customer: any) => ({
          value: customer.id.toString(),
          label: customer.name
        }));
        setCustomerOptions(formattedOptions);
      } catch (error) {
        console.error("Error loading customers:", error);
      }
    };

    fetchCustomers();
  }, []);
  const submitItem = async () => {
    if (!validateForm()) {
      showErrorToast(t('vouchers:validation.fillAllFields'));
      return;
    }
    setLoading(true);
    setErrors({});

    try {
      const response = await adminApi.call('/voucher', 'POST', formData);

      if (!response.success) {
        showErrorToast(response.error || t('vouchers:messages.createError'));
        return;
      }

      showSuccessToast(t('vouchers:messages.createSuccess'));
      setFormData({
        code: "",
        voucherType: 0,
        value: 0,
        numberOfUse: 0,
        maxAmount: -1,
        from: "",
        to: "",
        isActive: false,
        isPublic: false,
        autoApply: false,
        customers: "",
      });
      setSelectedCustomers([]);
    } catch (error: any) {
      showErrorToast(t('common:error') + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbs = [
    { path: '/vouchers', label: t('vouchers:title') },
    { path: '/vouchers/create', label: t('vouchers:createVoucher') }
  ];

  return (
    <PageLayout
      title={t('vouchers:createVoucher')}
      subtitle={t('vouchers:subtitle')}
      breadcrumbs={breadcrumbs}
      className={isRTL ? 'rtl' : 'ltr'}
    >

      <div className="row">
        <div className="mb-3 col-md-6">
          <label className="form-label">{t('vouchers:fields.code')} <span className="required">*</span></label>
          <input
            type="text"
            className={`form-control ${errors.code ? 'is-invalid' : ''}`}
            name="code"
            value={formData.code}
            onChange={handleInputChange}
            placeholder={t('vouchers:placeholders.code')}
            required
          />
          {errors.code && <div className="invalid-feedback">{errors.code}</div>}
        </div>

        <div className="mb-3 col-md-6">
          <label className="form-label">{t('vouchers:fields.type')}</label>
          <select
            className="form-control"
            name="voucherType"
            value={formData.voucherType}
            onChange={handleInputChange}
          >
            <option value={"0"}>{t('vouchers:types.percent')}</option>
            <option value={"1"}>{t('vouchers:types.amount')}</option>
          </select>
        </div>

        <div className="mb-3 col-md-6">
          <label className="form-label">{t('vouchers:fields.startDate')} <span className="required">*</span></label>
          <input
            type="date"
            className={`form-control ${errors.from ? 'is-invalid' : ''}`}
            name="from"
            value={formData.from}
            onChange={handleInputChange}
            placeholder={t('vouchers:placeholders.startDate')}
            required
          />
          {errors.from && <div className="invalid-feedback">{errors.from}</div>}
        </div>

        <div className="mb-3 col-md-6">
          <label className="form-label">{t('vouchers:fields.endDate')} <span className="required">*</span></label>
          <input
            type="date"
            className={`form-control ${errors.to ? 'is-invalid' : ''}`}
            name="to"
            value={formData.to}
            onChange={handleInputChange}
            placeholder={t('vouchers:placeholders.endDate')}
            required
          />
          {errors.to && <div className="invalid-feedback">{errors.to}</div>}
        </div>

        <div className="mb-3 col-md-6">
          <label className="form-label">{t('vouchers:fields.value')} <span className="required">*</span></label>
          <input
            type="number"
            className={`form-control ${errors.value ? 'is-invalid' : ''}`}
            name="value"
            value={formData.value}
            onChange={handleInputChange}
            placeholder={t('vouchers:placeholders.value')}
            min="0"
            step="0.01"
            required
          />
          {errors.value && <div className="invalid-feedback">{errors.value}</div>}
        </div>

        <div className="mb-3 col-md-6">
          <label className="form-label">{t('vouchers:table.numberOfUse')} <span className="required">*</span></label>
          <input
            type="number"
            className={`form-control ${errors.numberOfUse ? 'is-invalid' : ''}`}
            name="numberOfUse"
            value={formData.numberOfUse}
            onChange={handleInputChange}
            placeholder={t('vouchers:placeholders.usageLimit')}
            min="1"
            required
          />
          {errors.numberOfUse && <div className="invalid-feedback">{errors.numberOfUse}</div>}
        </div>

        {formData.voucherType === 0 && (
          <div className="mb-3 col-md-6">
            <label className="form-label">{t('vouchers:fields.maxAmount')}</label>
            <input
              type="number"
              className="form-control"
              name="maxAmount"
              value={formData.maxAmount}
              onChange={handleInputChange}
              placeholder={t('vouchers:placeholders.maxAmount')}
              step="0.01"
            />
            <small className="form-text text-muted">{t('vouchers:fields.maxAmountHint')}</small>
          </div>
        )}
        
        <div className="mb-3 col-md-3">
          <div className={`form-check mt-4 ${isRTL ? 'form-check-reverse' : ''}`}>
            <input
              type="checkbox"
              className="form-check-input"
              name="isActive"
              checked={formData.isActive}
              onChange={handleInputChange}
              id="isActive"
            />
            <label className="form-check-label" htmlFor="isActive">
              {t('vouchers:fields.isActive')}
            </label>
          </div>
        </div>

        <div className="mb-3 col-md-3">
          <div className={`form-check mt-4 ${isRTL ? 'form-check-reverse' : ''}`}>
            <input
              type="checkbox"
              className="form-check-input"
              name="isPublic"
              checked={formData.isPublic}
              onChange={handleInputChange}
              id="isPublic"
            />
            <label className="form-check-label" htmlFor="isPublic">
              {t('vouchers:fields.isPublic')}
            </label>
          </div>
        </div>

        <div className="mb-3 col-md-3">
          <div className={`form-check mt-4 ${isRTL ? 'form-check-reverse' : ''}`}>
            <input
              type="checkbox"
              className="form-check-input"
              name="autoApply"
              checked={formData.autoApply}
              onChange={handleInputChange}
              id="autoApply"
            />
            <label className="form-check-label" htmlFor="autoApply">
              {t('vouchers:fields.autoApply')}
            </label>
          </div>
        </div>

        <div className="mb-3 col-md-12">
          <label className="form-label">{t('vouchers:table.customers')}</label>
          <Select
            isMulti
            value={selectedCustomers}
            onChange={handleCustomerChange}
            options={customerOptions}
            placeholder={t('common:select')}
            isSearchable
            isClearable
            isRtl={isRTL}
            closeMenuOnSelect={false}
            className="react-select-container"
            classNamePrefix="react-select"
            noOptionsMessage={() => t('common:noOptions', 'No options available')}
            loadingMessage={() => t('common:loading', 'Loading...')}
          />
        </div>
      </div>

      <div className="form-actions">
        <ActionButton
          variant="primary"
          icon={HiCheck}
          onClick={submitItem}
          loading={loading}
          disabled={loading}
        >
          {loading ? t('vouchers:buttons.submitting') : t('vouchers:buttons.submit')}
        </ActionButton>
      </div>
    </PageLayout>
  );
};
